// src/utils/blogAnalytics.js

const GA4_MEASUREMENT_ID = "G-KXL99MJGPN";
const BLOG_ATTRIBUTION_KEY = "reignite_blog_attribution_v1";
const BLOG_ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const EVENT_ONCE_PREFIX = "reignite_ga_once_";

const clean = (value) => {
  if (value == null) return "";
  return String(value).trim();
};

const hasWindow = () => typeof window !== "undefined";

export const initGa4 = () => {
  if (!hasWindow()) return false;
  if (typeof window.gtag !== "function") return false;
  return true;
};

export const rememberBlogAttribution = ({
  blogPostId = "",
  blogSlug = "",
  categorySlug = "",
  primaryKeyword = "",
  ctaName = "",
} = {}) => {
  if (!hasWindow()) return;

  const current = getBlogAttribution({ allowExpired: true });
  const next = {
    blogPostId: clean(blogPostId) || current.blogPostId || "",
    blogSlug: clean(blogSlug) || current.blogSlug || "",
    categorySlug: clean(categorySlug) || current.categorySlug || "",
    primaryKeyword: clean(primaryKeyword) || current.primaryKeyword || "",
    ctaName: clean(ctaName) || current.ctaName || "",
    capturedAt: Date.now(),
  };

  try {
    localStorage.setItem(BLOG_ATTRIBUTION_KEY, JSON.stringify(next));
    sessionStorage.setItem(BLOG_ATTRIBUTION_KEY, JSON.stringify(next));
  } catch (error) {
    console.warn("[BlogAnalytics] Unable to save attribution", error);
  }
};

export const getBlogAttribution = ({ allowExpired = false } = {}) => {
  const empty = {
    blogPostId: "",
    blogSlug: "",
    categorySlug: "",
    primaryKeyword: "",
    ctaName: "",
    capturedAt: 0,
  };
  if (!hasWindow()) return empty;

  let raw = "";
  try {
    raw = sessionStorage.getItem(BLOG_ATTRIBUTION_KEY) ||
      localStorage.getItem(BLOG_ATTRIBUTION_KEY) || "";
  } catch {
    return empty;
  }
  if (!raw) return empty;

  try {
    const parsed = JSON.parse(raw);
    const capturedAt = Number(parsed?.capturedAt || 0);
    const expired = !capturedAt || Date.now() - capturedAt > BLOG_ATTRIBUTION_MAX_AGE_MS;
    if (expired && !allowExpired) {
      clearBlogAttribution();
      return empty;
    }
    return {
      blogPostId: clean(parsed?.blogPostId),
      blogSlug: clean(parsed?.blogSlug),
      categorySlug: clean(parsed?.categorySlug),
      primaryKeyword: clean(parsed?.primaryKeyword),
      ctaName: clean(parsed?.ctaName),
      capturedAt,
    };
  } catch {
    return empty;
  }
};

export const clearBlogAttribution = () => {
  if (!hasWindow()) return;
  try {
    localStorage.removeItem(BLOG_ATTRIBUTION_KEY);
    sessionStorage.removeItem(BLOG_ATTRIBUTION_KEY);
  } catch {}
};

const buildEventPayload = (extra = {}) => {
  const attribution = getBlogAttribution();
  const payload = {
    source: attribution.blogSlug ? "blog" : clean(extra.source),
    blog_post_id: clean(extra.blogPostId) || attribution.blogPostId,
    blog_slug: clean(extra.blogSlug) || attribution.blogSlug,
    category_slug: clean(extra.categorySlug) || attribution.categorySlug,
    primary_keyword: clean(extra.primaryKeyword) || attribution.primaryKeyword,
    cta_name: clean(extra.ctaName) || attribution.ctaName,
    assessment_id: clean(extra.assessmentId),
    report_type: clean(extra.reportType),
    payment_choice: clean(extra.paymentChoice),
    payment_mode: clean(extra.paymentMode),
  };

  Object.keys(payload).forEach((key) => {
    if (payload[key] === "" || payload[key] == null) delete payload[key];
  });
  return payload;
};

export const trackEvent = (eventName, extra = {}) => {
  if (!hasWindow() || typeof window.gtag !== "function") {
    console.warn("[BlogAnalytics] gtag unavailable:", eventName);
    return false;
  }
  window.gtag("event", eventName, buildEventPayload(extra));
  return true;
};

export const trackAttributedEvent = (eventName, extra = {}) => {
  const attribution = getBlogAttribution();
  if (!attribution.blogSlug) return false;
  return trackEvent(eventName, extra);
};

export const trackAttributedEventOnce = (eventName, uniqueKey, extra = {}) => {
  const attribution = getBlogAttribution();
  if (!attribution.blogSlug || !hasWindow()) return false;
  const onceKey = EVENT_ONCE_PREFIX + eventName + "_" + clean(uniqueKey || attribution.blogSlug);
  try {
    if (localStorage.getItem(onceKey) === "1") return false;
  } catch {}

  const sent = trackEvent(eventName, extra);
  if (sent) {
    try { localStorage.setItem(onceKey, "1"); } catch {}
  }
  return sent;
};

export const getBlogAttributionForApi = () => {
  const a = getBlogAttribution();
  if (!a.blogSlug) return {};
  return {
    blogPostId: a.blogPostId || null,
    blogSlug: a.blogSlug || null,
    categorySlug: a.categorySlug || null,
    primaryKeyword: a.primaryKeyword || null,
  };
};

export { GA4_MEASUREMENT_ID };
